import { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@chakra-ui/react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import { useCreateBankAccount, useUpdateBankAccount } from '../hooks/useBankAccounts'
import {
  isValidIban,
  formatIban,
  deduceBicFromIban,
  getBankName,
  isValidBic,
} from '../utils/iban'
import { BANK_ACCOUNT_TYPE_LABELS, BANK_ACCOUNT_TYPE_DESCRIPTIONS } from '../types'
import type { BankAccount, BankAccountType } from '../types'
import { toaster } from '@/shared/lib/toaster'

interface BankAccountFormModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
  account?: BankAccount
  prefilledType?: BankAccountType
  prefilledIsPrimary?: boolean
}

export function BankAccountFormModal({
  isOpen,
  onClose,
  residenceId,
  account,
  prefilledType,
  prefilledIsPrimary,
}: BankAccountFormModalProps) {
  const isEditMode = !!account

  const [formData, setFormData] = useState({
    label: '',
    account_type: 'current' as BankAccountType,
    iban: '',
    bic: '',
    bank_name: '',
    account_holder: '',
    is_primary: false,
  })

  const [ibanError, setIbanError] = useState<string | null>(null)
  const [bicError, setBicError] = useState<string | null>(null)
  const ibanValidationTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (isOpen) {
      if (account) {
        setFormData({
          label: account.label,
          account_type: account.account_type,
          iban: account.iban,
          bic: account.bic || '',
          bank_name: account.bank_name,
          account_holder: account.account_holder,
          is_primary: account.is_primary ?? false,
        })
      } else {
        setFormData({
          label: '',
          account_type: prefilledType || 'current',
          iban: '',
          bic: '',
          bank_name: '',
          account_holder: '',
          is_primary: prefilledIsPrimary ?? false,
        })
      }
      setIbanError(null)
      setBicError(null)
    }
  }, [isOpen, account, prefilledType, prefilledIsPrimary])

  const createBankAccount = useCreateBankAccount()
  const updateBankAccount = useUpdateBankAccount()

  const handleIbanChange = (value: string) => {
    const cleanValue = value.replace(/\s/g, '').toUpperCase()
    setFormData({ ...formData, iban: cleanValue })

    // Effacer l'erreur pendant la saisie pour ne pas frustrer l'utilisateur
    setIbanError(null)

    // Annuler le timeout précédent
    if (ibanValidationTimeoutRef.current) {
      clearTimeout(ibanValidationTimeoutRef.current)
    }

    // Si l'IBAN est vide, pas de validation
    if (!cleanValue) {
      return
    }

    // Déclencher une validation après 3 secondes d'inactivité
    ibanValidationTimeoutRef.current = setTimeout(() => {
      if (cleanValue && !isValidIban(cleanValue)) {
        setIbanError('IBAN invalide')
      }
    }, 3000)

    // Auto-complétion BIC et nom de la banque pour IBAN français et européens supportés
    // Pays supportés: FR, DE, BE, NL, ES, GB, LT
    const countryCode = cleanValue.substring(0, 2)
    const supportedCountries = ['FR', 'DE', 'BE', 'NL', 'ES', 'GB', 'LT']
    if (supportedCountries.includes(countryCode) && cleanValue.length >= 9) {
      const deducedBic = deduceBicFromIban(cleanValue)
      const deducedBankName = getBankName(cleanValue)

      if (deducedBic && !formData.bic) {
        setFormData((prev) => ({ ...prev, bic: deducedBic }))
      }
      if (deducedBankName && !formData.bank_name) {
        setFormData((prev) => ({ ...prev, bank_name: deducedBankName }))
      }
    }
  }

  const handleIbanBlur = () => {
    // Annuler le timeout de validation si présent
    if (ibanValidationTimeoutRef.current) {
      clearTimeout(ibanValidationTimeoutRef.current)
    }

    // Validation immédiate à la perte de focus
    if (formData.iban && !isValidIban(formData.iban)) {
      setIbanError('IBAN invalide')
    }
  }

  // Nettoyer le timeout au démontage du composant
  useEffect(() => {
    return () => {
      if (ibanValidationTimeoutRef.current) {
        clearTimeout(ibanValidationTimeoutRef.current)
      }
    }
  }, [])

  const handleBicChange = (value: string) => {
    const cleanValue = value.replace(/\s/g, '').toUpperCase()
    setFormData({ ...formData, bic: cleanValue })

    if (cleanValue && !isValidBic(cleanValue)) {
      setBicError('BIC invalide (format: 8 ou 11 caractères)')
      return
    }

    setBicError(null)
  }

  const handleSubmit = async () => {
    // Validation
    if (!formData.label || !formData.iban || !formData.bank_name || !formData.account_holder) {
      toaster.create({ title: 'Erreur', description: 'Veuillez remplir tous les champs obligatoires', type: 'error' })
      return
    }

    if (!isValidIban(formData.iban)) {
      toaster.create({ title: 'Erreur', description: 'IBAN invalide', type: 'error' })
      return
    }

    if (formData.bic && !isValidBic(formData.bic)) {
      toaster.create({ title: 'Erreur', description: 'BIC invalide', type: 'error' })
      return
    }

    try {
      if (isEditMode) {
        await updateBankAccount.mutateAsync({
          id: account.id,
          ...formData,
        })
        toaster.create({ title: 'Succès', description: 'Compte bancaire modifié avec succès', type: 'success' })
      } else {
        await createBankAccount.mutateAsync({
          residence_id: residenceId,
          ...formData,
        })
        toaster.create({ title: 'Succès', description: 'Compte bancaire créé avec succès', type: 'success' })
      }
      handleClose()
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de l\'opération'
      toaster.create({ title: 'Erreur', description: message, type: 'error' })
    }
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="lg">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Modifier le compte bancaire' : 'Ajouter un compte bancaire'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Flex direction="column" gap={4}>
              {/* Type de compte */}
              <Field label="Type de compte" required>
                <NativeSelectRoot>
                  <NativeSelectField
                    value={formData.account_type}
                    onChange={(e) => setFormData({ ...formData, account_type: e.target.value as BankAccountType })}
                  >
                    <option value="current">{BANK_ACCOUNT_TYPE_LABELS.current}</option>
                    <option value="savings">{BANK_ACCOUNT_TYPE_LABELS.savings}</option>
                    <option value="alur_reserve">{BANK_ACCOUNT_TYPE_LABELS.alur_reserve}</option>
                  </NativeSelectField>
                </NativeSelectRoot>
                <Text fontSize="xs" color="fg.muted" mt={1}>
                  {BANK_ACCOUNT_TYPE_DESCRIPTIONS[formData.account_type]}
                </Text>
              </Field>

              {/* Libellé */}
              <Field label="Libellé du compte" required>
                <Input
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="Ex: Compte courant Crédit Mutuel"
                />
              </Field>

              {/* IBAN */}
              <Field label="IBAN" required error={ibanError || undefined}>
                <Input
                  value={formData.iban}
                  onChange={(e) => handleIbanChange(e.target.value)}
                  onBlur={handleIbanBlur}
                  placeholder="FR76 1234 5678 9012 3456 7890 123"
                  fontFamily="mono"
                />
                {formData.iban && !ibanError && (
                  <Text fontSize="xs" color="fg.success" mt={1}>
                    IBAN valide: {formatIban(formData.iban)}
                  </Text>
                )}
              </Field>

              <Flex gap={4}>
                {/* BIC */}
                <Box flex={1}>
                  <Field label="BIC / SWIFT" error={bicError || undefined}>
                    <Input
                      value={formData.bic}
                      onChange={(e) => handleBicChange(e.target.value)}
                      placeholder="CMCIFR2A"
                      fontFamily="mono"
                    />
                  </Field>
                </Box>

                {/* Banque */}
                <Box flex={1}>
                  <Field label="Nom de la banque" required>
                    <Input
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      placeholder="Ex: Crédit Mutuel"
                    />
                  </Field>
                </Box>
              </Flex>

              {/* Titulaire */}
              <Field label="Titulaire du compte" required>
                <Input
                  value={formData.account_holder}
                  onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                  placeholder="Ex: Copropriété Music Halls"
                />
              </Field>

              {/* Compte principal */}
              <Field>
                <Flex align="center" gap={2}>
                  <input
                    type="checkbox"
                    checked={formData.is_primary}
                    onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                  />
                  <Text fontSize="sm">Définir comme compte principal</Text>
                </Flex>
                <Text fontSize="xs" color="fg.muted" mt={1}>
                  Le compte principal est utilisé par défaut pour les nouvelles opérations
                </Text>
              </Field>

              <Box p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" color="blue.700">
                  Un compte 512 sera automatiquement créé dans le plan comptable pour ce compte bancaire.
                </Text>
              </Box>
            </Flex>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={createBankAccount.isPending || updateBankAccount.isPending}
            >
              {isEditMode ? 'Modifier' : 'Créer le compte'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
